import { constants, FileUpload, Icon, notification } from "@findemdev/maui";
import { agenticUrl } from "@findemdev/findem-helpers";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FILE_RESOURCE_CATEGORY_EXEC,
  FILE_RESOURCE_TYPE,
  useGetAccountResourceFileQuery,
  useUpdateAccountResourceFileMutation,
  useUploadAccountResourceFileMutation,
} from "../../../../services/accountResourceFiles";
import {
  BehaviourSection,
  BehaviourTextarea,
  CancelButton,
  CurrentFileInfo,
  CurrentFileLabel,
  CurrentFileName,
  DownloadButton,
  ErrorText,
  UploadButton,
  UploadModalBody,
  UploadModalFooter,
  UploadModalFrame,
} from "./ResourceFileUploadModal.styled";

const { colors, dimensions } = constants;

const MODAL_CLOSE_ICON_SIZE =
  Number.parseInt(dimensions.spacing["2"], 10) +
  Number.parseInt(dimensions["border-radius"].xs, 10);

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;
// Matches the backend INSTRUCTIONS_MAX_LENGTH cap.
const INSTRUCTIONS_MAX_LENGTH = 4000;

const MODAL_CLOSE_ICON = (
  <Icon
    icon="close"
    $size={MODAL_CLOSE_ICON_SIZE}
    $color={colors["grey-500"]}
  />
);

// Allowed file types: pptx for structured templates, png/jpg for visual references.
// When the browser supplies a MIME type, both the extension AND MIME must match.
// When file.type is empty (some OS/browser combos omit it), fall back to extension-only.
const ALLOWED_TYPES: ReadonlyArray<{ ext: string; mime: string }> = [
  { ext: ".pptx", mime: "application/vnd.openxmlformats-officedocument.presentationml.presentation" },
  { ext: ".png", mime: "image/png" },
  { ext: ".jpg", mime: "image/jpeg" },
  { ext: ".jpeg", mime: "image/jpeg" },
];

interface IApiErrorData {
  status?: number;
  data?: {
    message?: string;
    error_code?: string;
  };
}

const isApiError = (err: unknown): err is IApiErrorData =>
  typeof err === "object" && err !== null && ("data" in err || "status" in err);

const getUploadErrorMessage = (err: unknown): string => {
  if (!isApiError(err)) return "Upload failed. Please try again.";
  // 403 no longer signals an admin gate — any user can upload.
  // Treat it as a generic permission/auth error.
  if (err.status === 403) return "You don't have permission to upload files.";
  if (err.status === 404) return "Org not found.";
  return err.data?.message ?? "Upload failed. Please try again.";
};

const formatDate = (dateStr: string): string => {
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
};

interface IResourceFileUploadModalProps {
  joid: string;
  onClose: () => void;
  visible: boolean;
  /** Use-case category passed to the API. Defaults to FILE_RESOURCE_CATEGORY_EXEC ("exec_project"). */
  category?: string;
}

export const ResourceFileUploadModal = ({
  joid,
  onClose,
  visible,
  category = FILE_RESOURCE_CATEGORY_EXEC,
}: IResourceFileUploadModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  // GET /file_resources returns an array — derive the most recent record for this category.
  const { data: fileList } = useGetAccountResourceFileQuery(
    { joid, resource_type: FILE_RESOURCE_TYPE, category },
    { skip: joid === "" },
  );

  const currentFileData = useMemo(() => {
    if (!fileList || fileList.length === 0) return null;
    return fileList.reduce((latest, item) => {
      const latestTs = new Date(latest.updated_at ?? latest.created_at).getTime();
      const itemTs = new Date(item.updated_at ?? item.created_at).getTime();
      return itemTs > latestTs ? item : latest;
    });
  }, [fileList]);

  const [uploadAccountResourceFile, { isLoading: isUploading }] =
    useUploadAccountResourceFileMutation();
  const [updateAccountResourceFile, { isLoading: isSavingInstructions }] =
    useUpdateAccountResourceFileMutation();

  // Template behaviour instructions — prefilled from the current record, reset
  // whenever the active record (or its saved value) changes. Optional: empty
  // means nothing is sent to the agent and generation behaviour is unchanged.
  const savedInstructions = currentFileData?.instructions ?? "";
  const [instructions, setInstructions] = useState(savedInstructions);
  useEffect(() => {
    setInstructions(savedInstructions);
  }, [currentFileData?.resource_id, savedInstructions]);
  const isInstructionsDirty = instructions !== savedInstructions;

  const handleFileChange = useCallback((file: File) => {
    const lowerName = file.name.toLowerCase();
    const matchedType = ALLOWED_TYPES.find((t) => lowerName.endsWith(t.ext));
    const isValid = matchedType
      ? file.type
        ? file.type === matchedType.mime
        : true
      : false;

    if (!isValid) {
      setValidationError("Only .pptx, .png, or .jpg files are supported");
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setValidationError("File must be 25 MB or smaller");
      setSelectedFile(null);
      return;
    }

    setValidationError("");
    setUploadError("");
    setSelectedFile(file);
  }, []);

  const handleRemoveFiles = useCallback(() => {
    setSelectedFile(null);
    setValidationError("");
    setUploadError("");
  }, []);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setValidationError("");
    setUploadError("");
    setInstructions(savedInstructions);
    onClose();
  }, [onClose, savedInstructions]);

  const handleSubmit = useCallback(async () => {
    if (!joid) return;

    setUploadError("");

    // Text-only save: no new file picked, but the behaviour text changed —
    // PUT the existing record in place (no re-upload). '' clears it.
    if (!selectedFile) {
      if (!isInstructionsDirty || !currentFileData?.resource_id) return;
      try {
        await updateAccountResourceFile({
          joid,
          resource_id: currentFileData.resource_id,
          resource_type: FILE_RESOURCE_TYPE,
          instructions: instructions.trim(),
        }).unwrap();
        notification.success({ message: "Template behaviour saved" });
        // The modal stays mounted while hidden — clear any stale file-pick
        // error so reopening doesn't show it.
        setValidationError("");
        onClose();
      } catch (err) {
        setUploadError(getUploadErrorMessage(err));
      }
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    // resource_type is set on formData by the service layer (accountResourceFilesApi query fn).
    // Do not append it here.
    // The textarea is prefilled from the current record, so uploading a
    // replacement carries the behaviour text onto the new record.
    if (instructions.trim()) formData.append("instructions", instructions);
    // Replacing the deck must not reset the account's New Slide configuration.
    if (currentFileData?.slide_options) {
      formData.append(
        "slide_options",
        JSON.stringify(currentFileData.slide_options)
      );
    }
    formData.append(
      "default_slide_enabled",
      String(currentFileData?.default_slide_enabled !== false)
    );

    try {
      // For singleton categories (e.g. exec_project) the BE supersedes the prior
      // active template server-side (retire-then-insert) on upload — the FE just
      // uploads the new one; no client-side delete of old records is needed.
      await uploadAccountResourceFile({
        joid,
        resource_type: FILE_RESOURCE_TYPE,
        category,
        formData,
      }).unwrap();

      notification.success({ message: "Template uploaded successfully" });
      setSelectedFile(null);
      onClose();
    } catch (err) {
      setUploadError(getUploadErrorMessage(err));
    }
  }, [
    category,
    currentFileData?.default_slide_enabled,
    currentFileData?.resource_id,
    currentFileData?.slide_options,
    instructions,
    isInstructionsDirty,
    joid,
    onClose,
    selectedFile,
    updateAccountResourceFile,
    uploadAccountResourceFile,
  ]);

  const handleDownload = useCallback(async () => {
    if (!joid || !currentFileData?.resource_id || !currentFileData?.file_name) return;

    setIsDownloading(true);
    const url = `${agenticUrl}/agentic/api/file_resources/${encodeURIComponent(currentFileData.resource_id)}/download?${new URLSearchParams({ joid }).toString()}`;

    try {
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = currentFileData.file_name;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch {
      notification.error({ message: "Failed to download template" });
    } finally {
      setIsDownloading(false);
    }
  }, [joid, currentFileData?.resource_id, currentFileData?.file_name]);

  const hasCurrentFile = currentFileData !== null;

  return (
    <UploadModalFrame
      closeIcon={MODAL_CLOSE_ICON}
      footer={
        <UploadModalFooter>
          <CancelButton htmlType="button" onClick={handleClose}>
            Cancel
          </CancelButton>
          <UploadButton
            disabled={
              !joid ||
              (!selectedFile && !(isInstructionsDirty && hasCurrentFile))
            }
            htmlType="button"
            loading={isUploading || isSavingInstructions}
            onClick={handleSubmit}
            type="primary"
          >
            {!selectedFile && isInstructionsDirty && hasCurrentFile
              ? "Save"
              : "Upload"}
          </UploadButton>
        </UploadModalFooter>
      }
      onCancel={handleClose}
      title="Upload Presentation Template"
      visible={visible}
      width={480}
    >
      <UploadModalBody>
        {hasCurrentFile && (
          <CurrentFileInfo>
            <CurrentFileLabel>Current file</CurrentFileLabel>
            <CurrentFileName>{currentFileData.file_name}</CurrentFileName>
            {currentFileData.updated_at && (
              <CurrentFileLabel>
                Last updated: {formatDate(currentFileData.updated_at)}
              </CurrentFileLabel>
            )}
            <DownloadButton
              disabled={isDownloading}
              htmlType="button"
              loading={isDownloading}
              onClick={handleDownload}
            >
              <Icon icon="download" $size={16} />
              Download
            </DownloadButton>
          </CurrentFileInfo>
        )}

        {/* FileUpload note: accept omitted — OS file-picker type-filter scan causes lag.
            handleFileChange validates extension + MIME and is the source of truth. */}
        <FileUpload
          inputKey="resource-file-upload"
          onChange={handleFileChange}
          onRemoveFiles={handleRemoveFiles}
          text="Click or drag a .pptx, .png, or .jpg template/screenshot here"
        />

        {(hasCurrentFile || selectedFile) && (
          <BehaviourSection>
            <CurrentFileLabel>Template behaviour (optional)</CurrentFileLabel>
            <BehaviourTextarea
              disabled={isSavingInstructions || isUploading}
              maxLength={INSTRUCTIONS_MAX_LENGTH}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g. Keep summaries to 3 bullets; highlight leadership experience first. Leave empty for default behaviour."
              value={instructions}
            />
          </BehaviourSection>
        )}

        {validationError && <ErrorText>{validationError}</ErrorText>}
        {uploadError && <ErrorText>{uploadError}</ErrorText>}
      </UploadModalBody>
    </UploadModalFrame>
  );
};
